import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import IosShareIcon from '@mui/icons-material/IosShare';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import { useSelector } from 'react-redux';

const DISMISS_KEY = 'tv-install-dismissed';
const DISMISS_FOR_MS = 1000 * 60 * 60 * 24 * 14;

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

function isIos() {
  const ua = window.navigator.userAgent || '';
  const iPhone = /iphone|ipad|ipod/i.test(ua);
  const iPadOs = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return iPhone || iPadOs;
}

function dismissedRecently() {
  const dismissed = Number(localStorage.getItem(DISMISS_KEY) || 0);
  return dismissed && Date.now() - dismissed < DISMISS_FOR_MS;
}

export default function InstallPrompt() {
  const isAuth = useSelector((state) => state.auth.isAuthenticated);
  const [deferred, setDeferred] = useState(() => window.__tvInstallPrompt || null);
  const [ios, setIos] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isStandalone() || dismissedRecently()) {
      return undefined;
    }

    const showNative = (event) => {
      setDeferred(event || window.__tvInstallPrompt || null);
      setIos(false);
      setOpen(true);
    };

    if (window.__tvInstallPrompt) {
      showNative(window.__tvInstallPrompt);
    }

    const onReady = () => showNative(window.__tvInstallPrompt);
    window.addEventListener('tv-install-ready', onReady);

    const timer = window.setTimeout(() => {
      if (!window.__tvInstallPrompt && isIos() && !isStandalone()) {
        setIos(true);
        setOpen(true);
      }
    }, 2200);

    const onInstalled = () => {
      window.__tvInstallPrompt = null;
      setOpen(false);
    };
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('tv-install-ready', onReady);
      window.removeEventListener('appinstalled', onInstalled);
      window.clearTimeout(timer);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setOpen(false);
  };

  const install = async () => {
    const promptEvent = deferred || window.__tvInstallPrompt;
    if (!promptEvent) {
      return;
    }
    promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    window.__tvInstallPrompt = null;
    setDeferred(null);
    if (choice.outcome === 'accepted') {
      setOpen(false);
    }
  };

  if (!open) {
    return null;
  }

  return (
    <Box
      role="dialog"
      aria-label="Install Trust Vault"
      sx={{
        position: 'fixed',
        zIndex: (theme) => theme.zIndex.snackbar,
        left: 12,
        right: 12,
        bottom: isAuth ? { xs: 'calc(68px + env(safe-area-inset-bottom))', md: 24 } : 'calc(16px + env(safe-area-inset-bottom))',
        mx: 'auto',
        maxWidth: 420,
        p: 1.75,
        borderRadius: 3,
        bgcolor: 'background.paper',
        color: 'text.primary',
        border: 1,
        borderColor: 'divider',
        boxShadow: '0 18px 50px rgba(0,0,0,0.28)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box
          component="img"
          src="/images/logos/trust-vault-app-icon.png"
          alt=""
          sx={{ width: 44, height: 44, borderRadius: 2, flexShrink: 0 }}
        />
        <Typography sx={{ flex: 1, fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 22, lineHeight: 1.05, fontWeight: 600 }}>
          Install Trust Vault
        </Typography>
        <IconButton aria-label="Dismiss install prompt" onClick={dismiss} size="small" sx={{ color: 'text.secondary' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      {ios ? (
        <Box sx={{ mt: 1.25 }}>
          <Typography sx={{ fontSize: 14, lineHeight: 1.45, color: 'text.secondary' }}>
            Add the app to your iPhone home screen.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1 }}>
            <IosShareIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography sx={{ fontSize: 14 }}>Tap Share</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
            <AddBoxOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography sx={{ fontSize: 14 }}>Then tap Add to Home Screen</Typography>
          </Box>
        </Box>
      ) : (
        <Typography sx={{ mt: 1.25, fontSize: 14, lineHeight: 1.45, color: 'text.secondary' }}>
          Keep Trust Vault on your Android home screen and open it like an app.
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
        {!ios && (
          <Button
            variant="contained"
            onClick={install}
            sx={{ textTransform: 'none', borderRadius: 999, px: 2.5, bgcolor: 'primary.main', color: '#fff' }}
          >
            Install
          </Button>
        )}
        <Button onClick={dismiss} sx={{ textTransform: 'none', borderRadius: 999, color: 'text.secondary' }}>
          Not now
        </Button>
      </Box>
    </Box>
  );
}
